import merge from 'lodash/merge'
import get from 'lodash/get'
import * as requestActionTypes from 'services/Request/actionTypes'
import * as locationActionTypes from 'services/Location/actionTypes'
import find from 'lodash/find'
import concat from 'lodash/concat'
import reject from 'lodash/reject'
import * as actionTypes from './actionTypes'
import {
  STORE_SIZE,
  AREA_CODE,
  ALL_REGIONS_VALUE,
} from 'services/Stores/constants'

import { StoreFiltersState } from './typings'
import getConstants from '../../constants'

const constants = getConstants()

export type StateType = {
  results: Array<Components.Schemas.StoreList>
  totalCount?: number
  searchTerm?: string
  areaCode?: string
  filters: StoreFiltersState
  offset: number
  entities: {
    [storeId: string]: Components.Schemas.StoreList
  }
  selectedStore: number | string
  selectedMarker: number | null
}

type ActiveFilterType = { [key: string]: string | boolean }

const filters = (): StoreFiltersState => ({
  activeFiltersOrder: [],
  baby: false,
  kids: false,
  maternity: false,
  parking: false,
  wheelchairAccessibleParking: false,
  storeSize: '',
  men: false,
  openFrontInner: false,
  uniqloFlower: false,
  teen: false,
  utMe: false,
  reUniqloStudio: false,
  customOrderMen: false,
  customOrderWomen: false,
})

const initialState = (): StateType => ({
  results: [],
  totalCount: undefined,
  searchTerm: undefined,
  filters: filters(),
  areaCode: undefined,
  offset: 0,
  entities: {},
  selectedStore: '',
  selectedMarker: null,
})

const SINGLE_SELECTION_FILTERS = [AREA_CODE, STORE_SIZE]

// Utilities to deal with the active filters order array updates

const checkFilterTypeOfEntry = (entry: ActiveFilterType, type: string) =>
  Object.keys(entry)[0] === type

const updateActiveFiltersOrder = (
  currentActivationOrder: Array<ActiveFilterType>,
  payload: ActiveFilterType,
) => {
  const filterType = Object.keys(payload)[0]
  if (SINGLE_SELECTION_FILTERS.indexOf(filterType) >= 0) {
    // replace last filter selection for this filterType with current selection for radio options
    return reject(currentActivationOrder, entry =>
      checkFilterTypeOfEntry(entry, filterType),
    ).concat(payload[filterType] && payload[filterType] !== '' ? payload : [])
  } else {
    let updatedOrder = [...currentActivationOrder]
    // remove selection if present, otherwise add it to the array for checkbox options
    // updated the case when multiple filters values are selected from URL
    Object.keys(payload).forEach(key => {
      const existingEntry = find(updatedOrder, entry =>
        checkFilterTypeOfEntry(entry, key),
      )
      if (existingEntry) {
        updatedOrder = reject(updatedOrder, entry =>
          checkFilterTypeOfEntry(entry, key),
        )
      } else {
        updatedOrder = concat(updatedOrder, { [key]: payload[key] })
      }
    })
    return updatedOrder
  }
}

const removeAreaCodeFromActiveFilters = (
  currentActivationOrder: Array<ActiveFilterType>,
) =>
  reject(currentActivationOrder, entry =>
    checkFilterTypeOfEntry(entry, AREA_CODE),
  )

const StoresReducer = (state = initialState(), action: ActionType) => {
  switch (action.type) {
    case requestActionTypes.SUCCEEDED:
      switch (action.meta.sourceType) {
        case actionTypes.SEARCH_REQUESTED:
          return {
            ...state,
            offset: initialState().offset,
            results: get(action.payload.result, 'stores', []),
            totalCount: get(action.payload.result, 'pagination')['total'],
            entities: merge({}, {}),
          }
        case actionTypes.LOAD_MORE_RESULTS_REQUESTED:
        case actionTypes.SEARCH_ALL_STORES_REQUESTED:
          return {
            ...state,
            results:
              state.offset === 0
                ? get(action.payload.result, 'stores', [])
                : [...state.results, ...action.payload.result.stores],
            totalCount: get(action.payload.result, 'pagination')['total'],
            entities: merge({}, {}),
          }
        case actionTypes.DETAILS_REQUESTED:
          return {
            ...state,
            entities: merge({}, state.entities, action.payload.entities),
          }
        case locationActionTypes.GEO_REQUESTED:
          return {
            ...state,
            searchTerm: undefined,
            areaCode: constants.IS_EU ? state.areaCode : undefined,
            offset: initialState().offset,
            filters: {
              ...state.filters,
              activeFiltersOrder: constants.IS_EU
                ? state.filters.activeFiltersOrder
                : removeAreaCodeFromActiveFilters(
                    state.filters.activeFiltersOrder,
                  ),
            },
          }
        default:
          return state
      }

    case locationActionTypes.GEO_UPDATED:
      return {
        ...state,
        searchTerm: undefined,
        areaCode: undefined,
        offset: initialState().offset,
        filters: {
          ...state.filters,
          activeFiltersOrder: removeAreaCodeFromActiveFilters(
            state.filters.activeFiltersOrder,
          ),
        },
        selectedMarker: null,
      }

    case actionTypes.SEARCH_TERM_UPDATED: {
      return {
        ...state,
        searchTerm: action.payload.searchTerm,
        offset: initialState().offset,
      }
    }

    case actionTypes.SEARCH_TERM_CLEARED: {
      return { ...state, searchTerm: undefined }
    }

    case actionTypes.AREA_UPDATED: {
      return {
        ...state,
        areaCode:
          action.payload.areaCode !== ALL_REGIONS_VALUE
            ? action.payload.areaCode
            : undefined,
        filters: {
          ...state.filters,
          activeFiltersOrder: updateActiveFiltersOrder(
            state.filters.activeFiltersOrder,
            action.payload,
          ),
        },
        offset: 0,
        selectedMarker: null,
      }
    }

    case actionTypes.FILTERS_UPDATED:
      return {
        ...state,
        offset: 0,
        filters: {
          ...state.filters,
          ...action.payload,
          activeFiltersOrder: updateActiveFiltersOrder(
            state.filters.activeFiltersOrder,
            action.payload,
          ),
        },
        selectedMarker: null,
      }
    case actionTypes.FILTERS_CLEARED:
      return {
        ...state,
        filters: initialState().filters,
        areaCode: undefined,
        selectedMarker: null,
      }
    case actionTypes.LOAD_MORE_RESULTS:
      return {
        ...state,
        offset: state.results.length,
      }

    case actionTypes.SELECTED_STORE_UPDATED:
      return {
        ...state,
        selectedStore: action.payload.storeId,
      }

    case actionTypes.SELECTED_MARKER_UPDATED:
      return {
        ...state,
        selectedMarker: action.payload.storeId,
      }

    case actionTypes.CLEAR_SELECTED_MARKER:
      return {
        ...state,
        selectedMarker: null,
      }

    case actionTypes.CLEAR_ALL: {
      const clearValue = initialState()
      if (action.payload.preserveAreaCode) {
        clearValue.areaCode = state.areaCode
        clearValue.filters.areaCode = state.filters.areaCode
        const areaActiveFilter = state.filters.activeFiltersOrder.find(
          filter => filter.areaCode,
        )

        if (areaActiveFilter) {
          clearValue.filters.activeFiltersOrder.push(areaActiveFilter)
        }
      }

      return clearValue
    }

    default:
      return state
  }
}

export default StoresReducer
